import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import Header from "../../commons/Header/Header";
import Sidebar from "../../commons/Sidebar/Sidebar";
import TablePagination from "../../commons/Pagination/TablePagination";
import DateRangeSelectButton from "../../commons/DateRangeSelectButton/DateRangeSelectButton";
import { handleApiCall } from "../../apis";
import { endpoints } from "../../utils/endpoints";
import { UI_CONSTANTS } from "../../utils/constants";

const formatMoney = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return `$${num.toFixed(2)}`;
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatDescription = (value) => {
  if (!value) return "-";
  return String(value)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const parseWalletLedger = (payload) => {
  const list = payload?.wallet_ledger;
  return Array.isArray(list) ? list : [];
};

function DriverWalletLedger() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const driverId = searchParams.get("driver_id") || location.state?.driverId || id || "";
  const driverName = location.state?.driverName || "Driver";

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fromdate, setFromdate] = useState(null);
  const [todate, setTodate] = useState(null);
  const [showAppliedRange, setShowAppliedRange] = useState(false);
  const limit = 10;

  const fetchWalletLedger = useCallback(async () => {
    if (!driverId) {
      setRows([]);
      setTotalPages(1);
      return;
    }

    setLoading(true);
    try {
      const params = new URLSearchParams({
        driver_id: driverId,
        page: String(page),
        limit: String(limit),
      });

      if (fromdate && todate) {
        params.set("fromdate", fromdate);
        params.set("todate", todate);
      }

      const response = await handleApiCall(
        "get",
        `${endpoints.getDriverWalletLedger}?${params.toString()}`
      );

      if (response?.data?.code === UI_CONSTANTS.API_CODES.SUCCESS) {
        const payload = response?.data?.data;
        setRows(parseWalletLedger(payload));
        setTotalPages(
          Number(
            payload?.pagination?.total_pages ||
              payload?.pagination?.totalPages ||
              1
          ) || 1
        );
      } else {
        setRows([]);
        setTotalPages(1);
      }
    } catch (error) {
      console.error("Error fetching driver wallet ledger:", error);
      setRows([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [driverId, fromdate, todate, page, limit]);

  useEffect(() => {
    fetchWalletLedger();
  }, [fetchWalletLedger]);

  const handleApplyDateRange = (start, end) => {
    setFromdate(start);
    setTodate(end);
    setShowAppliedRange(true);
    setPage(1);
  };

  const handleResetDateRange = () => {
    setFromdate(null);
    setTodate(null);
    setShowAppliedRange(false);
    setPage(1);
  };

  const handleBack = () => {
    navigate(location.state?.from || `/driver-managment/view-driver/${id}`);
  };

  return (
    <div className="container-scroller">
      <Header />
      <div className="container-fluid page-body-wrapper">
        <Sidebar />
        <div className="main-panel">
          <div className="content-wrapper">
            <div className="heading mb-4 d-flex align-items-center">
              <h3 className="text-default weight-400 my-0">View Ledger</h3>
            </div>

            <div className="mb-3">
              <button type="button" className="btn btn-white rounded-lg" onClick={handleBack}>
                Back
              </button>
            </div>

            <div className="mb-4 px-0 py-2">
              <div className="card-heading d-flex align-items-center mb-3 pb-2">
                <h4 className="my-0">{driverName}</h4>
                <div className="ml-auto">
                  <DateRangeSelectButton
                    appliedFrom={fromdate}
                    appliedTo={todate}
                    onApply={handleApplyDateRange}
                    onReset={handleResetDateRange}
                  />
                </div>
              </div>

              {showAppliedRange && fromdate && todate && (
                <p className="text-muted small mb-3">
                  Showing data from {fromdate} to {todate}
                </p>
              )}

              <div className="col-md-12 tab-content row px-0 mx-0">
                <div className="table-responsive px-0">
                  <table className="table table-striped trim admin-wallet-table">
                    <thead>
                      <tr>
                        <th className="text-left">Date</th>
                        <th className="text-left">Order / Payout ID</th>
                        <th className="text-left">Description</th>
                        <th className="text-right">Credit</th>
                        <th className="text-right">Debit</th>
                        <th className="text-right">Remaining Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="6" className="text-center py-4">
                            <div className="spinner-border" role="status">
                              <span className="sr-only">
                                {UI_CONSTANTS.BUTTON_LABELS.LOADING}
                              </span>
                            </div>
                          </td>
                        </tr>
                      ) : rows.length > 0 ? (
                        rows.map((item, index) => (
                          <tr
                            key={`${item.order_or_payout_id}-${item.createdAt}-${index}`}
                          >
                            <td className="text-left">{formatDateTime(item.createdAt)}</td>
                            <td className="text-left">
                              {item.order_or_payout_id || "-"}
                            </td>
                            <td className="text-left">
                              {formatDescription(item.description)}
                            </td>
                            <td className="text-right">{formatMoney(item.credit)}</td>
                            <td className="text-right">{formatMoney(item.debit)}</td>
                            <td className="text-right">
                              {formatMoney(item.remaining_balance)}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="6" className="text-center py-4">
                            {UI_CONSTANTS.EMPTY_STATES.NO_DATA_FOUND}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <TablePagination
                page={page}
                totalPages={totalPages}
                onPageChange={setPage}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DriverWalletLedger;
